import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { externalAddressHint } from '@brimo/shared';
import { api, errorMessage } from '../../../lib/api';
import type { InvitationPreview, InvitationsResult, OrganizationBillingSeats, RoleItem } from './types';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface InviteRow {
  id: number;
  email: string;
  roleId: string;
  ownBilling: boolean;
  /** Server answer of the last send („Für diese Adresse gibt es bereits einen Nutzer."). */
  serverError: string | null;
}

export interface RowState {
  error: string | null;
  hint: string | null;
  valid: boolean;
}

let nextRowId = 1;

/**
 * State of the team step (Admin 10 / M4): rows with e-mail, role and own billing, live validation and the
 * „Externe Adresse" hint (B2), sending through `POST /invitations` with per-row answers.
 */
export function useTeamInvites() {
  const roles = useQuery({ queryKey: ['roles'], queryFn: () => api<{ items: RoleItem[] }>('/roles') });
  const preview = useQuery({
    queryKey: ['invitations', 'preview'],
    queryFn: () => api<InvitationPreview>('/invitations/preview'),
  });
  const billing = useQuery({
    queryKey: ['billing', 'organization'],
    queryFn: () => api<OrganizationBillingSeats>('/settings/organization/billing'),
  });
  const defaultRole = roles.data?.items.find((role) => role.key === 'advisor') ?? roles.data?.items[0];
  const [rows, setRows] = useState<InviteRow[]>(() => [
    { id: nextRowId++, email: '', roleId: '', ownBilling: false, serverError: null },
  ]);
  const [seatLabel, setSeatLabel] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const domains = useMemo(() => new Set(preview.data?.domains ?? []), [preview.data]);

  function stateOf(row: InviteRow): RowState {
    const email = row.email.trim().toLowerCase();
    if (email === '') return { error: null, hint: null, valid: false };
    if (!EMAIL_PATTERN.test(email)) {
      return { error: 'Bitte geben Sie eine gültige E-Mail-Adresse an.', hint: null, valid: false };
    }
    const duplicate = rows.some((other) => other.id !== row.id && other.email.trim().toLowerCase() === email);
    if (duplicate) return { error: 'Diese Adresse steht schon in der Liste.', hint: null, valid: false };
    if (row.serverError) return { error: row.serverError, hint: null, valid: false };
    const domain = email.split('@')[1] ?? '';
    const external = domains.size > 0 && !domains.has(domain);
    return { error: null, hint: external ? externalAddressHint : null, valid: true };
  }

  const states = rows.map(stateOf);
  const count = states.filter((state) => state.valid).length;
  const blocked = states.some((state) => state.error !== null);

  function roleOf(row: InviteRow): string {
    return row.roleId || defaultRole?.id || '';
  }

  function update(id: number, patch: Partial<InviteRow>) {
    setRows((current) =>
      current.map((row) =>
        row.id === id ? { ...row, ...patch, serverError: 'email' in patch ? null : row.serverError } : row,
      ),
    );
  }

  function add() {
    setRows((current) => [
      ...current,
      { id: nextRowId++, email: '', roleId: '', ownBilling: false, serverError: null },
    ]);
  }

  function remove(id: number) {
    setRows((current) => {
      const next = current.filter((row) => row.id !== id);
      return next.length > 0
        ? next
        : [{ id: nextRowId++, email: '', roleId: '', ownBilling: false, serverError: null }];
    });
  }

  /** Sends every valid row; resolves true when all of them were sent. */
  async function send(): Promise<boolean> {
    const toSend = rows.filter((row, index) => states[index]?.valid);
    if (toSend.length === 0 || blocked) return false;
    setSending(true);
    setFailure(null);
    try {
      const result = await api<InvitationsResult>('/invitations', {
        method: 'POST',
        body: {
          source: 'wizard',
          rows: toSend.map((row) => ({
            email: row.email.trim().toLowerCase(),
            roleId: roleOf(row),
            hasOwnBilling: row.ownBilling,
          })),
        },
      });
      setSeatLabel(result.seats.label);
      const errors = new Map(
        result.rows
          .filter((answer) => answer.status === 'error')
          .map((answer) => [answer.email.toLowerCase(), answer.message ?? errorMessage(null)]),
      );
      setRows((current) => {
        const kept = current
          .filter((row) => {
            const email = row.email.trim().toLowerCase();
            return email === '' || errors.has(email) || !toSend.some((sent) => sent.id === row.id);
          })
          .map((row) => ({ ...row, serverError: errors.get(row.email.trim().toLowerCase()) ?? null }));
        return kept.length > 0
          ? kept
          : [{ id: nextRowId++, email: '', roleId: '', ownBilling: false, serverError: null }];
      });
      return errors.size === 0;
    } catch (error) {
      setFailure(errorMessage(error));
      return false;
    } finally {
      setSending(false);
    }
  }

  return {
    roles,
    preview,
    rows,
    states,
    count,
    blocked,
    sending,
    failure,
    seatLabel: seatLabel ?? billing.data?.plan.usage.label ?? null,
    roleOf,
    update,
    add,
    remove,
    send,
  };
}

/** „1 Einladung senden" / „2 Einladungen senden". */
export function sendLabel(count: number): string {
  return count === 1 ? '1 Einladung senden' : `${count} Einladungen senden`;
}
